import React, { useState, useEffect, useCallback } from 'react';
import { 
    Box, Typography, Paper, Tabs, Tab, List, ListItem, ListItemText, 
    ListItemAvatar, Avatar, IconButton, Dialog, 
    DialogTitle, DialogContent, DialogActions, Button, CircularProgress,
    TextField, FormControl, InputLabel, Select, MenuItem, Fab, Grid, Chip
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

// Iconos
import MailIcon from '@mui/icons-material/Mail';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplyIcon from '@mui/icons-material/Reply';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';

dayjs.locale('es');
const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

const authFetch = (url, options = {}) => {
    const token = localStorage.getItem('token');

    return fetch(url, {
        ...options,
        headers: {
            ...(options.headers || {}),
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
};

const MailboxPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // --- ESTADOS DE LA BANDEJA ---
    const [mainTabValue, setMainTabValue] = useState(0); // 0: Recibidos, 1: Enviados
    const [receivedMessages, setReceivedMessages] = useState([]);
    const [sentMessages, setSentMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // --- ESTADOS PARA VER DETALLES ---
    const [openDetail, setOpenDetail] = useState(false);
    const [selectedMsg, setSelectedMsg] = useState(null);

    // --- ESTADOS PARA REDACTAR MENSAJE ---
    const [openCompose, setOpenCompose] = useState(false);
    const [msgTab, setMsgTab] = useState(0); // 0: Interno, 1: Externo
    const [users, setUsers] = useState([]); 
    const [selectedRecipient, setSelectedRecipient] = useState(null);
    const [messageSubject, setMessageSubject] = useState('');
    const [messageBody, setMessageBody] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [macros, setMacros] = useState([]); // NUEVO: Estado para macros

    // 1. Cargar Bandeja
    const fetchMailbox = useCallback(async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            const res = await authFetch(`${API_URL}/api/messages/mailbox/${user.id}`);
            if (res.ok) {
                const data = await res.json();
                setReceivedMessages(data.received || []);
                setSentMessages(data.sent || []);
            }
        } catch (e) { console.error(e); } finally { setLoading(false); }
    }, [user?.id]);

    // 2. Cargar Usuarios y Macros
    const fetchInitialData = useCallback(async () => {
        try {
            const [usersRes, macrosRes] = await Promise.all([
                authFetch(`${API_URL}/api/users`),
				authFetch(`${API_URL}/api/macros`)
            ]);

            if (usersRes.ok) {
                const data = await usersRes.json();
                setUsers(data.filter(u => u.id !== user?.id));
            }
            if (macrosRes.ok) {
                setMacros(await macrosRes.json());
            }
        } catch (e) { console.error("Error cargando datos:", e); }
    }, [user?.id]);

    useEffect(() => { 
        fetchMailbox(); 
        fetchInitialData();
    }, [fetchMailbox, fetchInitialData]);

    // --- PROCESADOR DE MACROS ---
    const processMacro = (content, data) => {
        return content
            .replace(/{{paciente}}/g, data.patientName || 'Paciente')
            .replace(/{{doctor}}/g, data.doctorName || 'especialista')
            .replace(/{{fecha}}/g, data.date || '')
            .replace(/{{hora}}/g, data.time || '')
            .replace(/{{clinica}}/g, 'Luiatro Health');
    };

    // --- ENVÍO DE MENSAJES ---
    const handleSendMessage = async () => {
        const isInternal = msgTab === 0;
        
        if (isInternal) {
            if (!selectedRecipient?.id || !messageBody.trim()) {
                return alert("Complete los campos obligatorios (Destinatario interno y Mensaje).");
            }
        } else {
            const emailAddress = typeof selectedRecipient === 'string' 
                ? selectedRecipient 
                : selectedRecipient?.email;
                
            if (!emailAddress || !messageBody.trim()) {
                return alert("Complete los campos obligatorios (Email y Mensaje).");
            }
        }

        setIsSending(true);

        try {
            const endpoint = isInternal ? '/api/messages/internal' : '/api/messages/external';
            
            const payload = isInternal ? {
                senderId: user.id,
                receiverId: selectedRecipient.id,
                subject: messageSubject || 'Sin Asunto',
                content: messageBody
            } : {
                senderId: user.id,
                to: typeof selectedRecipient === 'string' ? selectedRecipient : selectedRecipient.email,
                subject: messageSubject || 'Comunicación Médica',
                content: messageBody,
                doctorPhone: '584120000000'
            };

            const res = await authFetch(`${API_URL}${endpoint}`, {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                alert(isInternal ? "✅ Mensaje interno enviado." : "✅ Correo electrónico enviado.");
                setMessageBody('');
                setMessageSubject('');
                setSelectedRecipient(null);
                setOpenCompose(false);
                fetchMailbox();
            } else {
                const error = await res.text();
                alert("❌ Error al enviar mensaje.");
                console.error(error);
            }
        } catch (e) { 
            console.error(e);
            alert("❌ Error de conexión."); 
        } finally { 
            setIsSending(false); 
        }
    };

    // --- FUNCIONES DE BANDEJA ---
    const handleOpenMessage = async (msg) => {
        setSelectedMsg(msg);
        setOpenDetail(true);

        if (mainTabValue === 0 && !msg.isRead) {
            try {
                await authFetch(`${API_URL}/api/messages/read/${msg.id}`, { method: 'PATCH' });
                setReceivedMessages(prev => prev.map(m => m.id === msg.id ? { ...m, isRead: true } : m));
            } catch (e) { console.error(e); }
        }
    };

    const handleDeleteMessage = async (id, e) => {
        e.stopPropagation(); 
        if (!window.confirm("¿Eliminar registro?")) return;
        try {
            const res = await authFetch(`${API_URL}/api/messages/${id}`, { method: 'DELETE' });
            if (res.ok) fetchMailbox();
        } catch (e) { console.error(e); }
    };

    const handleQuickReply = (text) => {
        setOpenDetail(false);
        setOpenCompose(true);
        // Pre-llenar destinatario si es posible (Lógica básica)
        if (selectedMsg?.sender) {
            setMsgTab(0); // Asumir interno si hay sender object
            setSelectedRecipient(users.find(u => u.id === selectedMsg.senderId) || null);
        }
        setMessageSubject(`RE: ${selectedMsg?.subject || ''}`);
        setMessageBody(text + "\n\n");
    };

    if (loading && receivedMessages.length === 0 && sentMessages.length === 0) {
        return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /><Typography sx={{ mt: 2 }}>Cargando buzón...</Typography></Box>;
    }

    return (
        <Box sx={{ p: 3, maxWidth: '1000px', margin: '0 auto', position: 'relative' }}>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconButton onClick={() => navigate('/dashboard')}><ArrowBackIcon /></IconButton>
                <Typography variant="h4" fontWeight="bold" color="primary">Buzón Médico</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Fab color="primary" variant="extended" onClick={() => setOpenCompose(true)}>
                    <AddIcon sx={{ mr: 1 }} /> Redactar
                </Fab>
            </Box>

            <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Tabs value={mainTabValue} onChange={(_, v) => { setMainTabValue(v); setOpenDetail(false); }} variant="fullWidth">
                    <Tab icon={<MailIcon />} label={`Recibidos (${receivedMessages.filter(m => !m.isRead).length})`} />
                    <Tab icon={<SendIcon />} label="Enviados" />
                </Tabs>

                <List sx={{ py: 0, minHeight: '400px', bgcolor: 'white' }}>
                    {(mainTabValue === 0 ? receivedMessages : sentMessages).length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
                            <MailIcon sx={{ fontSize: 60, opacity: 0.1 }} />
                            <Typography>Sin mensajes</Typography>
                        </Box>
                    ) : (
                        (mainTabValue === 0 ? receivedMessages : sentMessages).map((msg) => (
                            <ListItem 
                                key={msg.id} 
                                onClick={() => handleOpenMessage(msg)} // El click ahora es directo
                                sx={{ 
                                    cursor: 'pointer', // Indica que se puede cliquear
                                    bgcolor: (mainTabValue === 0 && !msg.isRead) ? '#f0f7ff' : 'inherit',
                                    borderBottom: '1px solid #eee',
                                    '&:hover': { bgcolor: '#f5f5f5' }
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: msg.type === 'internal' ? 'primary.main' : 'success.main' }}>
                                        {msg.type === 'internal' ? <PersonIcon /> : <AlternateEmailIcon />}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText 
                                    primary={<Typography sx={{ fontWeight: (mainTabValue === 0 && !msg.isRead) ? 'bold' : 'normal' }}>{msg.subject || '(Sin Asunto)'}</Typography>} 
                                    secondary={`${dayjs(msg.createdAt).format('DD/MM HH:mm')} - ${mainTabValue === 0 ? 'De:' : 'Para:'} ${msg.sender?.name || msg.externalEmail || 'Externo'}`} 
                                />
                                <IconButton color="error" onClick={(e) => handleDeleteMessage(msg.id, e)}><DeleteIcon /></IconButton>
                            </ListItem>
                        ))
                    )}
                </List>
            </Paper>

            {/* --- DETALLES DEL MENSAJE --- */}
            <Dialog open={openDetail} onClose={() => setOpenDetail(false)} fullWidth maxWidth="sm">
                {selectedMsg && (
                    <>
                        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><MarkEmailReadIcon /><Typography variant="h6">Mensaje</Typography></Box>
                            <IconButton onClick={() => setOpenDetail(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Typography variant="caption" color="text.secondary">ASUNTO:</Typography>
                            <Typography variant="h6" sx={{ mb: 2 }}>{selectedMsg.subject}</Typography>
                            <Typography variant="caption" color="text.secondary">CONTENIDO:</Typography>
                            <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1, minHeight: '150px' }}>
                                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{selectedMsg.content}</Typography>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                            <Button variant="outlined" onClick={() => setOpenDetail(false)}>Cerrar</Button>
                            {mainTabValue === 0 && (
                                <Button variant="contained" startIcon={<ReplyIcon />} onClick={() => handleQuickReply("Responderé en breve")}>Responder</Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* --- REDACTAR NUEVO --- */}
            <Dialog open={openCompose} onClose={() => setOpenCompose(false)} fullWidth maxWidth="sm">
                <DialogTitle>Redactar Mensaje</DialogTitle>
                <DialogContent dividers>
                    <Tabs value={msgTab} onChange={(_, v) => { setMsgTab(v); setSelectedRecipient(null); }} sx={{ mb: 2 }}>
                        <Tab label="Interno (Staff)" />
                        <Tab label="Email (Paciente)" />
                    </Tabs>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {msgTab === 0 ? (
                            <FormControl fullWidth>
                                <InputLabel>Destinatario Interno</InputLabel>
                                <Select
                                    value={selectedRecipient || ''}
                                    label="Destinatario Interno"
                                    onChange={(e) => setSelectedRecipient(e.target.value)}
                                >
                                    {users.map(u => (
                                        <MenuItem key={u.id} value={u}>{u.name} ({u.role})</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <TextField 
                                fullWidth 
                                label="Email del Destinatario" 
                                type="email"
                                value={typeof selectedRecipient === 'string' ? selectedRecipient : ''}
                                onChange={(e) => setSelectedRecipient(e.target.value)}
                                placeholder="ejemplo@paciente.com"
                            />
                        )}

                        <TextField 
                            fullWidth label="Asunto" 
                            value={messageSubject} onChange={(e) => setMessageSubject(e.target.value)}
                        />
                        
                        {/* --- BARRA DE MACROS (NUEVO) --- */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ mr: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                                Plantillas:
                            </Typography>
                            {macros.map(m => (
                                <Chip 
                                    key={m.id}
                                    label={m.name}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    onClick={() => {
                                        const text = processMacro(m.content, {
                                            patientName: typeof selectedRecipient === 'string' ? 'Paciente' : (selectedRecipient?.name || 'Colega'),
                                            doctorName: user?.name,
                                            date: dayjs().format('DD/MM/YYYY'),
                                            time: dayjs().format('HH:mm')
                                        });
                                        setMessageBody(prev => (prev ? prev + '\n' : '') + text);
                                    }}
                                    clickable
                                />
                            ))}
                            <IconButton size="small" onClick={() => navigate('/config')} title="Configurar Macros">
                                <SettingsIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <TextField 
                            fullWidth label="Mensaje" multiline rows={4} 
                            value={messageBody} onChange={(e) => setMessageBody(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCompose(false)} color="inherit">Cancelar</Button>
                    <Button 
                        onClick={handleSendMessage} 
                        variant="contained" 
                        endIcon={isSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        disabled={isSending}
                    >
                        {isSending ? 'Enviando...' : 'Enviar'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default MailboxPage;